import type React from "react";
import { useState } from "react";

import { swSettings } from "app/common/services/swSettings";
import { allTrackers } from "app/services/track/track";

import EmailSentScreen from "../EmailSentScreen";
import InviteTeamMatesScreen from "../InviteTeamMatesScreen/InviteTeamMatesScreen";
import { useAllowedDomains, useRequestAccess, useTotalSeats } from "../utils";

import { StyledModal } from "./InviteUsersModal.styled";

interface IInviteUsersModalProps {
    open: boolean;
    onClose: () => void;
}

const InviteUsersModal: React.FC<IInviteUsersModalProps> = ({ open, onClose }) => {
    const [emails, setEmails] = useState<string[]>([]);
    const [emailsSent, setEmailsSent] = useState(false);

    const {
        data: { AvailableAllocations, IsUnlimited } = { AvailableAllocations: 0, IsUnlimited: false },
        isLoading: isSeatsLoading,
    } = useTotalSeats({
        enabled: open,
        staleTime: 0,
        cacheTime: 0,
    });

    const { data: domainsData = [], isLoading: isDomainsLoading } = useAllowedDomains({
        enabled: open,
        staleTime: 0,
        cacheTime: 0,
    });

    const { mutate: inviteUsers, status: inviteUsersStatus, reset: resetInviteUsers } = useRequestAccess();
    const isInviting = inviteUsersStatus !== "idle";

    const tmpAllocations = AvailableAllocations || 0;
    const remainingSeatsTotal = tmpAllocations < 0 ? 0 : tmpAllocations;
    const isUnlimitedSeats = IsUnlimited || false;

    const countRemainingSeats = (addedEmails: number) => {
        if (isUnlimitedSeats) {
            return Number.MAX_SAFE_INTEGER;
        } else {
            return remainingSeatsTotal - addedEmails;
        }
    };

    const remainingSeatsCount = countRemainingSeats(emails.length);

    const isLoading = isSeatsLoading || isDomainsLoading || isInviting;

    const handleClose = () => {
        setEmails([]);
        setEmailsSent(false);
        allTrackers.dispatchTrackEvent("Compare", "open", "Header");
        onClose();
        resetInviteUsers();
    };

    const handleAddEmail = (email: string) => {
        setEmails((prev) => [...prev, email]);
        allTrackers.dispatchTrackEvent("InviteUsersV2", "add_email", email);
    };

    const handleRemoveEmail = (email: string) => {
        setEmails((prev) => prev.filter((e) => e !== email));
        allTrackers.dispatchTrackEvent("InviteUsersV2", "remove_email", email);
    };

    const handleSendInvites = () => {
        inviteUsers(
            {
                fullName: `${swSettings.user.firstname} ${swSettings.user.lastname}`,
                emails,
            },
            {
                onSuccess: () => {
                    setEmailsSent(true);
                },
            },
        );
    };
    // invite/request-access
    return (
        <StyledModal
            isOpen={open}
            customStyles={{ overlay: { zIndex: 1071 } }}
        >
            <div key={`invite-modal-${open}`}>
                {emailsSent ? (
                    <EmailSentScreen
                        onClose={handleClose}
                        emails={emails}
                    />
                ) : (
                    <InviteTeamMatesScreen
                        onClose={handleClose}
                        onSendInvites={handleSendInvites}
                        isLoading={isLoading}
                        remainingSeatsCount={remainingSeatsCount}
                        remainingSeatsTotal={remainingSeatsTotal}
                        isUnlimitedSeats={isUnlimitedSeats}
                        allowedDomains={domainsData}
                        selectedEmails={emails}
                        onRemoveEmail={handleRemoveEmail}
                        handleAddEmail={handleAddEmail}
                    />
                )}
            </div>
        </StyledModal>
    );
};

export default InviteUsersModal;
